import React, { useState } from 'react';
import { DIALOGUES } from '@/data/dialogues';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

interface DialogueSystemProps {
  npcId: string;
  npcName: string;
  onClose: () => void;
}

export const DialogueSystem: React.FC<DialogueSystemProps> = ({ npcId, npcName, onClose }) => {
  const [currentDialogueId, setCurrentDialogueId] = useState('');
  const [romancePoints, setRomancePoints] = useState(0);

  React.useEffect(() => {
    const firstDialogue = DIALOGUES.find((d) => d.npcId === npcId);
    if (firstDialogue) {
      setCurrentDialogueId(firstDialogue.id);
    }
  }, [npcId]);

  const currentDialogue = DIALOGUES.find((d) => d.id === currentDialogueId);

  if (!currentDialogue) {
    return null;
  }

  const handleOptionSelect = (nextId?: string, points?: number) => {
    if (points) {
      setRomancePoints(romancePoints + points);
    }

    if (nextId) {
      setCurrentDialogueId(nextId);
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-end justify-center z-50 p-4">
      <Card className="w-full max-w-2xl bg-slate-800 border-orange-500 p-6 rounded-t-lg">
        {/* NPC Name and Romance Points */}
        <div className="flex justify-between items-center mb-4 pb-4 border-b border-orange-500">
          <h2 className="text-2xl font-bold text-orange-500">{npcName}</h2>
          {romancePoints > 0 && (
            <div className="text-pink-400 font-semibold">
              Romance: <span className="text-white">{romancePoints}</span>
            </div>
          )}
        </div>

        {/* Dialogue Text */}
        <p className="text-white text-lg mb-6 leading-relaxed italic">{currentDialogue.text}</p>

        {/* Options */}
        <div className="space-y-3 mb-6">
          {currentDialogue.options.map((option, index) => (
            <Button
              key={index}
              onClick={() =>
                handleOptionSelect(option.nextDialogueId, option.romancePoints)
              }
              className="w-full justify-start text-left h-auto py-3 px-4 bg-slate-700 hover:bg-orange-600 text-white border border-gray-600 hover:border-orange-600 transition"
            >
              <span className="flex justify-between items-center w-full">
                <span>{option.text}</span>
                {option.romancePoints && (
                  <span className="text-pink-300 text-sm ml-2">+{option.romancePoints}</span>
                )}
              </span>
            </Button>
          ))}
        </div>

        {/* Close Button */}
        <Button
          onClick={onClose}
          className="w-full bg-red-900 hover:bg-red-800 text-white font-semibold"
        >
          End Conversation
        </Button>
      </Card>
    </div>
  );
};
